import { useState, useMemo, useCallback, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";

import "./styles/SolarCalculator.css";
import CalculatorInputs from "./CalculatorInputs";
import SystemDetails from "./SystemDetails";
import LoadingSpinner from "../LoadingSpinner";
import SavingsSummary from "./SavingsSummary";
import EmailCapture from "../EmailCapture";
import { avgCostPerKWByState, solarProductionFactor } from "./constants";

// Lazy load chart components
const SolarSavings = lazy(() => import("./SolarSavings"));
const YearlyComparisonChart = lazy(() => import("./YearlyComparisonChart"));

function calculateLoanPayment(
  principal: number,
  annualRate: number,
  years: number
) {
  const r = annualRate / 12;
  const n = years * 12;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
}

export default function SolarCalculator() {
  const navigate = useNavigate();

  const [state, setState] = useState("Florida");
  const [monthlyBill, setMonthlyBill] = useState("180");
  const [offsetPercent, setOffsetPercent] = useState("100");
  const [systemLife, setSystemLife] = useState("25");
  const [overrideSize, setOverrideSize] = useState("");
  const [isFinanced, setIsFinanced] = useState(false);
  const [pricePerWatt, setPricePerWatt] = useState("2.75");
  const [utilityRate, setUtilityRate] = useState("0.14");

  const handleUpgradeToDetailed = useCallback(() => {
    // Carry the visitor's inputs into the quote form so they never re-type them.
    navigate("/quote", {
      state: { fromCalculator: true, usState: state, monthlyBill },
    });
  }, [navigate, state, monthlyBill]);

  const results = useMemo(() => {
    const avgCost =
      parseFloat(pricePerWatt) || avgCostPerKWByState[state] || 2.75;
    const bill = parseFloat(monthlyBill) || 0;
    const offset = parseFloat(offsetPercent) || 0;
    const life = parseFloat(systemLife) || 0;
    const prodFactor = solarProductionFactor[state] || 1350;
    const rate = parseFloat(utilityRate) || 0.14;

    const annualUsageKWh = (bill * 12) / rate;
    const offsetKWh = annualUsageKWh * (offset / 100);
    const estimatedSystemSizeKW = offsetKWh / prodFactor;

    const finalSystemSize = overrideSize
      ? parseFloat(overrideSize) || 0
      : estimatedSystemSizeKW;

    const grossSystemCost = finalSystemSize * avgCost * 1000;
    // The 30% federal residential solar tax credit (IRC §25D) expired for
    // systems installed after Dec 31, 2025, so 2026 cash/loan buyers get $0.
    const taxCredit = 0;
    const finalSystemCost = grossSystemCost - taxCredit;

    const annualProductionKWh = finalSystemSize * prodFactor;
    const actualOffsetKWh = Math.min(annualProductionKWh, offsetKWh);

    const annualSavings = actualOffsetKWh * rate;
    const totalSavings = annualSavings * life;

    const monthlyLoanPayment = isFinanced
      ? calculateLoanPayment(finalSystemCost, 0.0499, 20)
      : 0;

    const paybackPeriod = finalSystemCost / annualSavings;
    const roi = ((totalSavings - finalSystemCost) / finalSystemCost) * 100;

    const pieChartData = [
      {
        name: isFinanced ? "Financed Loan Cost" : "System Cost",
        value: isFinanced ? monthlyLoanPayment * 12 * life : finalSystemCost,
      },
      { name: `${life}-Year Savings`, value: totalSavings },
    ];

    const yearlyComparisonData = Array.from({ length: life }, (_, i) => {
      const year = i + 1;
      const utilityCost = bill * 12 * Math.pow(1.05, i);
      const solarCost = isFinanced
        ? monthlyLoanPayment * 12
        : i === 0
        ? finalSystemCost
        : 0;

      return {
        year,
        solar: solarCost,
        utility: utilityCost,
      };
    });

    return {
      finalSystemSize,
      annualUsageKWh,
      grossSystemCost,
      taxCredit,
      finalSystemCost,
      monthlyLoanPayment,
      annualSavings,
      totalSavings,
      annualProductionKWh,
      actualOffsetKWh,
      paybackPeriod,
      roi,
      pieChartData,
      yearlyComparisonData,
      pricePerWatt: avgCost,
    };
  }, [
    state,
    monthlyBill,
    offsetPercent,
    systemLife,
    overrideSize,
    isFinanced,
    pricePerWatt,
    utilityRate,
  ]);

  return (
    <div className="solar-calculator">
      <header className="calc-hero">
        <h1>Solar Savings Calculator</h1>
        <p>Estimate your ideal system size, cost, and payback in seconds.</p>
      </header>
      <div className="calculator-grid">
        {/* Row 1: Inputs & Details */}
        <div>
          <CalculatorInputs
            state={state}
            monthlyBill={monthlyBill}
            offsetPercent={offsetPercent}
            systemLife={systemLife}
            overrideSize={overrideSize}
            isFinanced={isFinanced}
            pricePerWatt={pricePerWatt}
            onStateChange={setState}
            onMonthlyBillChange={setMonthlyBill}
            onOffsetPercentChange={setOffsetPercent}
            onSystemLifeChange={setSystemLife}
            onOverrideSizeChange={setOverrideSize}
            onFinancedChange={setIsFinanced}
            onPricePerWattChange={setPricePerWatt}
            utilityRate={utilityRate}
            onUtilityRateChange={setUtilityRate}
            avgCostPerKWByState={avgCostPerKWByState}
          />
        </div>

        <div className="system-details">
          <SystemDetails
            results={{
              finalSystemSize: results.finalSystemSize,
              annualProductionKWh: results.annualProductionKWh,
              actualOffsetKWh: results.actualOffsetKWh,
              grossSystemCost: results.grossSystemCost,
              taxCredit: results.taxCredit,
              finalSystemCost: results.finalSystemCost,
              solarLoanMonthly: results.monthlyLoanPayment,
              annualSavings: results.annualSavings,
              totalSavings: results.totalSavings,
              pricePerWatt: results.pricePerWatt,
            }}
            systemLife={systemLife}
            isFinanced={isFinanced}
          />
        </div>

        {/* Row 2: Pie Chart */}
        <div className="pie-chart">
          <Suspense fallback={<LoadingSpinner />}>
            <SolarSavings pieChartData={results.pieChartData} />
          </Suspense>
        </div>

        {/* Row 3: Yearly Savings Chart */}
        <div className="yearly-comparison-chart">
          <Suspense fallback={<LoadingSpinner />}>
            <YearlyComparisonChart data={results.yearlyComparisonData} />
          </Suspense>
        </div>

        {/* Oversizing warning — production beyond your usage gets settled at
            the utility's avoided-cost rate (~3¢/kWh in FL), not retail. */}
        {results.annualProductionKWh > results.annualUsageKWh * 1.02 && (
          <div className="oversize-warning">
            <strong>Heads up: this system is oversized.</strong> It would produce{" "}
            {Math.round(
              (results.annualProductionKWh / results.annualUsageKWh - 1) * 100
            )}
            % more than you use in a year. Under net metering, that annual surplus
            is paid out at the utility's avoided-cost rate — roughly 3¢/kWh in
            Florida, not the retail rate this calculator credits you inside
            your usage. Sizing to ~100% of your annual kWh usually beats
            oversizing.{" "}
            <a href="/blog/florida-net-metering-explained-2026">
              How the annual true-up works →
            </a>
          </div>
        )}

        {/* Row 4: Summary */}
        <div className="savings-summary">
          <SavingsSummary
            monthlyBill={parseFloat(monthlyBill)}
            offsetPercent={parseFloat(offsetPercent)}
            systemLife={parseFloat(systemLife)}
            finalSystemSize={results.finalSystemSize}
            finalSystemCost={results.finalSystemCost}
            annualSavings={results.annualSavings}
            totalSavings={results.totalSavings}
            monthlyLoanPayment={results.monthlyLoanPayment}
            paybackPeriod={results.paybackPeriod}
            roi={results.roi}
            isFinanced={isFinanced}
            pricePerWatt={results.pricePerWatt}
          />
        </div>

        {/* Row 5: CTA */}
        <div className="detailed-analysis-button">
          <p className="cta-lead-in">
            Like these numbers? The only way to know your real ones is a quote
            for your actual roof.
          </p>
          <button onClick={handleUpgradeToDetailed} className="upgrade-button">
            Get My Exact Numbers — Free →
          </button>
          <p className="cta-microcopy">
            Takes about 2 minutes · your state and bill carry over · no
            obligation
          </p>
        </div>

        {/* Honest assumptions — the trust move sleazy calculators won't make */}
        <div className="calc-assumptions">
          <h3>What this calculator assumes (so you can check our math)</h3>
          <ul>
            <li>
              Electricity at the rate you set (default $0.14/kWh), with utility
              prices rising 5%/yr in the comparison chart.
            </li>
            <li>
              <strong>No federal tax credit</strong> — the 30% credit expired
              for systems installed after Dec 31, 2025. Calculators still
              baking it in are inflating your savings by thousands.{" "}
              <a href="/blog/is-solar-worth-it-florida-2026">
                Is solar still worth it? →
              </a>
            </li>
            <li>
              Production based on your state's typical solar output per kW;
              your roof's direction and shade will move this.
            </li>
            <li>
              Financing at 4.99% APR over 20 years — watch for hidden dealer
              fees behind "low APR" loans.{" "}
              <a href="/blog/how-to-pay-for-solar-florida-2026">
                Cash vs. loan vs. lease →
              </a>
            </li>
          </ul>
        </div>

        <div className="calc-email-capture">
          <EmailCapture
            list="fl-solar-alerts"
            source="calculator"
            title="Not ready to pull the trigger yet?"
            blurb="Rates, net metering, and equipment prices all move. Get one short email when something changes the math you just ran."
            buttonLabel="Keep me posted"
          />
        </div>
      </div>
    </div>
  );
}
